import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild, ViewContainerRef } from "@angular/core";
import { LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ContextMenuService } from "../../services/context-menu.service";

/**
 * NOTE: this component is updated version extracted from angular-cesium
 *
 * new cesium html component is used and mechanism was simplified
 *
 * This component is used to inject the component that is passed to the ContextMenuService when opening a context menu.
 * It shouldn't be used directly.
 *
 * usage:
 * ```typescript
 * // We want to open the context menu on mouse right click.
 * // Register to mouse right click with the MapEventsManager
 * this.mapEventsManager.register({ event: CesiumEvent.RIGHT_CLICK, pick: PickOptions.NO_PICK })
 *    .subscribe(event => {
 *       const position = this.coordinateConverter.screenToCartesian3(event.movement.endPosition, true);
 *       if (!position) {
 *         return;
 *       }
 *       // Open the context menu on the position that was clicked and pass some data to MyCustomContextMenuComponent.
 *       this.contextMenuService.open(
 *         MyCustomContextMenuComponent,
 *         position,
 *         { data: { items: ['New Track', 'Change Map', 'Context Menu', 'Do Something'] } }
 *       )
 *    });
 *
 * ```
 */

@UntilDestroy()
@Component({
    selector: "dtm-map-cesium-context-menu-wrapper",
    template: `
        <dtm-map-html-cesium-component [coordinates]="contextMenuService.position">
            <ng-container #contextMenuContainer></ng-container>
        </dtm-map-html-cesium-component>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class CesiumContextMenuWrapperComponent implements OnInit {
    @ViewChild("contextMenuContainer", { read: ViewContainerRef })
    private viewContainerRef: ViewContainerRef | undefined;

    protected readonly contextMenuService = inject(ContextMenuService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    public ngOnInit() {
        this.contextMenuService.contextMenuChangeNotifier
            .pipe(untilDestroyed(this))
            .subscribe(() => this.changeDetectorRef.detectChanges());

        this.contextMenuService.onOpen.pipe(untilDestroyed(this)).subscribe(() => {
            this.viewContainerRef?.clear();
            if (!this.contextMenuService.content) {
                return;
            }
            const componentRef = this.viewContainerRef?.createComponent(this.contextMenuService.content);
            componentRef?.setInput("data", this.contextMenuService.options?.data);
        });

        this.contextMenuService.onClose.pipe(untilDestroyed(this)).subscribe(() => {
            this.viewContainerRef?.clear();
        });
    }
}
